import {
  REFERENCE_WIDTH,
  normalizeOverlay,
  stageHeight,
  titleBoxStyle,
  sanitizeCss,
  googleFontsHref,
  effectiveCss,
} from "@serie3/mymediaplanner/Modules/Social/Domain/overlayRenderer";

/**
 * Draws the title on an image. Used by the editor and by every post preview.
 *
 * The stage is always REFERENCE_WIDTH wide and scaled down with a transform, which
 * is the whole fidelity trick: layout happens at 1080px here exactly as it does in
 * the headless renderer, and only the raster scale differs. A `font-size: 64px` in
 * the user's CSS therefore looks the same here as in the published image.
 */
const OverlayView = ({
  overlay,
  title,
  src,
  imageSize,
  displayWidth,
  /** CSS inherited when the image has none of its own (group default). */
  defaultCss = null,
  ignoreEnabled = false,
  titleRef = null,
  onTitlePointerDown = null,
  selected = false,
  style = {},
}) => {
  const config = normalizeOverlay(overlay);
  const height = stageHeight(imageSize?.width, imageSize?.height);
  const scale = displayWidth / REFERENCE_WIDTH;
  const text = String(title || "").trim();
  const show = (config.enabled || ignoreEnabled) && text !== "";
  const css = effectiveCss(config, defaultCss);
  const fontsHref = show ? googleFontsHref(css) : null;

  const interactive = typeof onTitlePointerDown === "function";

  return (
    <div
      style={{
        position: "relative",
        width: `${displayWidth}px`,
        height: `${height * scale}px`,
        overflow: "hidden",
        ...style,
      }}
    >
      {/* The renderer pulls the same fonts, so the editor must use them too or
          the published text would reflow. */}
      {fontsHref && <link rel="stylesheet" href={fontsHref} />}

      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: `${REFERENCE_WIDTH}px`,
          height: `${height}px`,
          transform: `scale(${scale})`,
          transformOrigin: "top left",
        }}
      >
        {src && (
          <img
            src={src}
            alt=""
            draggable={false}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              userSelect: "none",
            }}
          />
        )}

        {show && (
          <>
            {/* Scoped to this id so the CSS cannot reach anything else on the page. */}
            <style>{`#ov-title{${sanitizeCss(css)}}`}</style>
            <div
              id="ov-title"
              ref={titleRef}
              onPointerDown={interactive ? onTitlePointerDown : undefined}
              style={{
                ...titleBoxStyle(config),
                ...(interactive
                  ? {
                      cursor: "move",
                      outline: selected ? "3px solid #2f6fed" : "none",
                      outlineOffset: "2px",
                    }
                  : {}),
              }}
            >
              {text}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OverlayView;
